import { SalesActionsType } from 'api-hooks/general/model';
import { SaleModel } from 'api-hooks/sale/common';
import { AuthorizationRules, NavigationRoutes } from 'common/constants';
import { SaleStatusEnum } from 'common/constants/enum';
import { FormAutoSaver } from 'common/helpers/form-auto-saver';
import notification from 'components/common/notification';
import { PopoverMenuType } from 'components/popover';
import { useAuthorization } from 'hooks/use-authorization';
import useNavigation from 'hooks/use-navigation';
import useTranslation from 'next-translate/useTranslation';
import React from 'react';

export default function useSalesReschedule(
  type: SalesActionsType,
  data: SaleModel,
) {
  const { t } = useTranslation();
  const { can } = useAuthorization();
  const { navigate } = useNavigation();

  const status = data.status;
  const isAllowed = React.useMemo(() => {
    switch (status) {
      case SaleStatusEnum.Active:
      case SaleStatusEnum.Completed:
      case SaleStatusEnum.OpenTicket:
      case SaleStatusEnum.Rescheduled:
        return true;
      default:
        return false;
    }
  }, [status]);

  const { authorizationRule, typeRefund } = React.useMemo(() => {
    switch (type) {
      case 'flight-sales':
        return {
          authorizationRule: AuthorizationRules.FlightSalesReschedule,
          typeRefund: 'flight' as const,
        };
      case 'hotel-sales':
        return {
          authorizationRule: AuthorizationRules.HotelSalesReschedule,
          typeRefund: 'hotel' as const,
        };
      case 'cruise-sales':
        return {
          authorizationRule: AuthorizationRules.CruiseSalesReschedule,
          typeRefund: 'cruise' as const,
        };
      case 'activity-sales':
        return {
          authorizationRule: AuthorizationRules.ActivitySalesReschedule,
          typeRefund: 'activity' as const,
        };
      //no reschedule
      case 'document-sales':
      case 'product-sales':
      case 'tour-sales':
        return {
          authorizationRule: AuthorizationRules.NoAuthorization,
          typeRefund: 'none' as const,
        };
    }
  }, [type]);

  const reschedulePermission =
    isAllowed &&
    can(authorizationRule) &&
    authorizationRule !== AuthorizationRules.NoAuthorization;

  const onReschedule = React.useCallback(() => {
    if (typeRefund === 'none') {
      notification.error({
        message: `Refund not available for ${type}`,
      });
      return;
    }

    FormAutoSaver.remove(NavigationRoutes.Reschedule);
    navigate(NavigationRoutes.Reschedule, {
      data,
      type: typeRefund,
    });
  }, [data, navigate, type, typeRefund]);

  const rescheduleAction: PopoverMenuType = {
    label: t('common:reschedule'),
    onClick: onReschedule,
    isAllowed: reschedulePermission,
    isError: false,
    key: `reschedule-${data.id}`,
  };

  return {
    onReschedule,
    rescheduleAction,
    reschedulePermission,
    authorizationRule,
  };
}
